import React, { useRef, useState, useCallback } from "react";
import { MapPin, Star, ArrowRight } from "lucide-react";
import { COMPANY, IMAGES } from "../../mock";
import { useEstimate } from "../EstimateContext";

export default function Hero() {
  const { openEstimate } = useEstimate();
  const sectionRef = useRef(null);
  const [pos, setPos] = useState({ x: 0.5, y: 0.5, px: 0, py: 0 });

  const onMove = useCallback((e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    setPos({ x: px / rect.width - 0.5, y: py / rect.height - 0.5, px, py });
  }, []);

  const imgTransform = `scale(1.12) translate(${pos.x * -26}px, ${pos.y * -26}px)`;
  const archTransform = `translate(${pos.x * 40}px, ${pos.y * 40}px)`;

  return (
    <section
      ref={sectionRef}
      onMouseMove={onMove}
      className="relative min-h-[100vh] flex items-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Modern kitchen renovation in Edmonton"
          className="w-full h-full object-cover transition-transform duration-300 ease-out will-change-transform"
          style={{ transform: imgTransform }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f2740]/95 via-[#0f2740]/82 to-[#0f2740]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/70 to-transparent" />
        {/* Mouse-follow spotlight */}
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-300"
          style={{
            background: `radial-gradient(520px circle at ${pos.px}px ${pos.py}px, rgba(47,159,230,0.22), transparent 45%)`,
          }}
        />
      </div>

      <div
        className="absolute -right-20 top-1/4 w-80 h-96 border border-white/10 shape-arch-top hidden lg:block transition-transform duration-300 ease-out"
        style={{ transform: archTransform }}
      />

      <div className="container-x relative z-10 pt-28 pb-20">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#2f9fe6]" />
            <span className="text-[#bfe0f5] tracking-[0.28em] text-xs sm:text-sm font-medium">{COMPANY.tagline}</span>
          </div>

          <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6">
            <span className="block font-light">Built for the</span>
            <span className="block font-bold text-[#2f9fe6]">Way You Live</span>
          </h1>

          <p className="text-white/85 text-lg sm:text-xl leading-relaxed mb-4 max-w-2xl">Top-quality renovations designed with care and built to last.</p>
          <p className="text-white/70 text-base leading-relaxed mb-9 max-w-2xl">
            Classic Interior Renovations is an Edmonton-based renovation company specializing in kitchens, bathrooms, basement renovations, full home remodeling, and custom interior projects.
          </p>

          <div className="flex flex-wrap items-center gap-4 mb-12">
            <button onClick={openEstimate} className="group px-8 py-4 rounded-full bg-[#2f9fe6] text-white font-semibold flex items-center gap-2 hover:bg-[#1f82c7] transition-all hover:shadow-xl hover:shadow-[#2f9fe6]/30">
              Get an Estimate
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <a href={COMPANY.reviewsUrl} target="_blank" rel="noreferrer" className="px-8 py-4 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">Read Our Reviews</a>
          </div>

          <div className="flex flex-wrap items-center gap-8">
            <a href={COMPANY.mapUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white/90 hover:text-white group">
              <span className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#2f9fe6] transition-colors"><MapPin className="w-5 h-5" /></span>
              <div>
                <div className="text-xs text-white/60 tracking-wide">FIND US ON</div>
                <div className="font-semibold">Google Maps</div>
              </div>
            </a>
            <div className="flex items-center gap-3 text-white/90">
              <div className="flex">{[...Array(5)].map((_, i) => (<Star key={i} className="w-4 h-4 fill-[#2f9fe6] text-[#2f9fe6]" />))}</div>
              <div>
                <div className="text-xs text-white/60 tracking-wide">5-STAR RATED</div>
                <div className="font-semibold">On Google</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2">
        <div className="w-6 h-10 rounded-full border-2 border-white/40 flex justify-center pt-2">
          <div className="w-1 h-2 rounded-full bg-white/70 animate-bounce" />
        </div>
      </div>
    </section>
  );
}
